// @ts-nocheck
import React, { useCallback, useEffect, useRef, useState } from "react";
import * as services from "../Services/services";
import { formatMinutes, resolveImageUrl, toEmbedUrl, watchedPercent } from "../utils/lessonMedia";
import { objectivesOf, passMarkFor, questionFeedback, quickReferenceOf, usableCaptions } from "../utils/lmsLearner";

/*
 * One lesson in Training: video, text, and the knowledge check — in whichever combination the
 * author chose (layout: text | video | video_text).
 *
 * Completion is decided by AVS OS, never here:
 *   - a lesson with questions completes when the check is passed (the lesson's pass mark, default
 *     80%, graded upstream; the answer key only comes back on a pass, while a failed attempt gets
 *     the explanation and the author's feedback on the options chosen);
 *   - a video-only lesson with an uploaded video completes once ~90% has played — this page reports
 *     the furthest point reached, and AVS OS refuses "mark as done" before then;
 *   - anything else completes when the learner says they have read or watched it.
 */

const ok = (resp) => resp?.data?.status === 1;
const payload = (resp) => resp?.data?.data;
const message = (resp, fallback) => resp?.data?.message || fallback;

/** How often to report progress while the video plays. */
const REPORT_EVERY_MS = 10_000;

/**
 * Caption files as same-origin blob URLs.
 *
 * A <track> pointing at another origin only loads when the <video> is crossOrigin="anonymous" — and
 * that would make the VIDEO itself need CORS from storage, so a bucket without it would stop playing.
 * Fetching the WebVTT text and handing the track a blob: URL keeps the video exactly as it was: if
 * the caption fetch fails (no CORS on the bucket, an expired link), the lesson simply has no captions.
 */
function useCaptionTracks(captions) {
  const [tracks, setTracks] = useState([]);
  const key = captions.map((c) => c.url).join("|");
  useEffect(() => {
    if (!captions.length || typeof fetch !== "function" || typeof URL?.createObjectURL !== "function") {
      setTracks([]);
      return undefined;
    }
    let cancelled = false;
    const made = [];
    Promise.all(
      captions.map(async (c) => {
        try {
          const res = await fetch(c.url, { credentials: "omit" });
          if (!res.ok) return null;
          const body = await res.text();
          if (!/^\uFEFF?WEBVTT/.test(body)) return null;
          const src = URL.createObjectURL(new Blob([body], { type: "text/vtt" }));
          made.push(src);
          return { ...c, src };
        } catch {
          return null;
        }
      })
    ).then((list) => {
      if (!cancelled) setTracks(list.filter(Boolean));
    });
    return () => {
      cancelled = true;
      made.forEach((u) => URL.revokeObjectURL(u));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  return tracks;
}

function UploadedVideo({ lesson, onProgress, onExpired }) {
  const ref = useRef(null);
  const captions = usableCaptions(lesson.captions);
  const tracks = useCaptionTracks(captions);
  // Available, not on by default (decision for workstream C): the learner turns them on.
  const [ccOn, setCcOn] = useState(false);

  useEffect(() => {
    const list = ref.current?.textTracks;
    if (!list) return;
    for (let i = 0; i < list.length; i++) list[i].mode = ccOn && i === 0 ? "showing" : "hidden";
  }, [ccOn, tracks]);
  const maxPct = useRef(lesson.progress?.videoPercent ?? 0);
  const lastSent = useRef(maxPct.current);
  const lastSentAt = useRef(0);
  const retried = useRef(false);

  const report = useCallback(
    (force) => {
      const now = Date.now();
      if (maxPct.current <= lastSent.current) return;
      if (!force && now - lastSentAt.current < REPORT_EVERY_MS) return;
      lastSent.current = maxPct.current;
      lastSentAt.current = now;
      onProgress(maxPct.current);
    },
    [onProgress]
  );

  const onTime = () => {
    const v = ref.current;
    if (!v) return;
    maxPct.current = watchedPercent(maxPct.current, v.currentTime, v.duration);
    report(false);
  };

  // Signed links expire after a few hours. One fresh link per page, resuming where they were.
  const onError = () => {
    if (retried.current) return;
    retried.current = true;
    onExpired(ref.current?.currentTime || 0);
  };

  return (
    <>
    <video
      ref={ref}
      className="w-100 rounded learning-video"
      src={lesson.videoPlaybackUrl}
      controls
      preload="metadata"
      playsInline
      controlsList="nodownload"
      onTimeUpdate={onTime}
      onPause={() => report(true)}
      onEnded={() => {
        maxPct.current = 100;
        report(true);
      }}
      onError={onError}
      onLoadedMetadata={(e) => {
        const at = Number(lesson.__resumeAt || 0);
        if (at > 0) e.currentTarget.currentTime = at;
      }}
      data-testid="learning-video"
    >
      {tracks.map((t) => (
        <track key={t.src} kind="captions" src={t.src} srcLang={t.language} label={t.label} />
      ))}
    </video>
      {tracks.length > 0 && (
        <div className="mt-2">
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm"
            aria-pressed={ccOn}
            onClick={() => setCcOn((on) => !on)}
            data-testid="learning-cc-toggle"
          >
            <i className="fa-solid fa-closed-captioning me-1" aria-hidden="true" />
            {ccOn ? "Hide captions" : "Show captions"}
          </button>
        </div>
      )}
    </>
  );
}

/** The transcript under the video, closed until asked for. Native <details> is keyboard-ready. */
function Transcript({ text }) {
  if (!text || !String(text).trim()) return null;
  return (
    <details className="mt-3 learning-transcript-text" data-testid="learning-transcript-text">
      <summary className="fw-semibold">Transcript</summary>
      <div className="learning-pre mt-2 small">{text}</div>
    </details>
  );
}

function Section({ title, icon, children }) {
  return (
    <section className="mb-4">
      <h2 className="h6 text-uppercase text-body-secondary fw-semibold mb-2">
        <i className={`${icon} me-2`} aria-hidden="true" />
        {title}
      </h2>
      {children}
    </section>
  );
}

function Check({ lesson, onResult }) {
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const resultRef = useRef(null);
  const passedBefore = Boolean(lesson.progress?.checkPassed);
  const passMark = passMarkFor(lesson, result);

  const submit = async () => {
    setBusy(true);
    setError(null);
    const resp = await services.lmsLessonCheck(
      lesson.id,
      lesson.checks.map((c) => ({ checkId: c.id, selectedIndex: answers[c.id] ?? null }))
    );
    setBusy(false);
    if (!ok(resp)) {
      setError(message(resp, "Your answers couldn't be checked, please try again."));
      return;
    }
    const r = payload(resp);
    setResult(r);
    onResult(r);
    // Move focus to the score so a keyboard or screen-reader user hears how it went.
    setTimeout(() => resultRef.current?.focus(), 0);
  };

  return (
    <Section title="Test your knowledge" icon="fa-solid fa-list-check">
      <p className="small text-body-secondary mb-3" data-testid="learning-pass-mark">
        Answer every question. You need {passMark}% to pass.
      </p>
      {passedBefore && !result && (
        <p className="small text-success-emphasis mb-3" data-testid="learning-check-passed-before">
          <i className="fa-solid fa-circle-check me-1" aria-hidden="true" />
          You've passed this already. You can take it again; it won't undo your pass.
        </p>
      )}
      {lesson.checks.map((c, qi) => {
        const fb = result ? questionFeedback(result, c.id, answers[c.id] ?? null) : null;
        const fbId = `q-${c.id}-feedback`;
        const hasFb = fb && (fb.correct !== null || fb.explanation || fb.chosenFeedback);
        return (
          <fieldset
            key={c.id}
            className="mb-3"
            aria-describedby={hasFb ? fbId : undefined}
            data-testid={`learning-question-${qi}`}
          >
            <legend className="fs-6 fw-semibold mb-2">
              {qi + 1}. {c.question ?? c.prompt}
            </legend>
            {(c.options || []).map((opt, oi) => (
              <div className="form-check" key={oi}>
                <input
                  className="form-check-input"
                  type="radio"
                  name={`q-${c.id}`}
                  id={`q-${c.id}-${oi}`}
                  checked={answers[c.id] === oi}
                  onChange={() => {
                    setAnswers((a) => ({ ...a, [c.id]: oi }));
                    setResult(null);
                  }}
                  data-testid={`learning-option-${qi}-${oi}`}
                />
                <label
                  className={`form-check-label ${fb && fb.correctIndex === oi ? "fw-semibold text-success-emphasis" : ""}`}
                  htmlFor={`q-${c.id}-${oi}`}
                >
                  {opt}
                  {fb && fb.correctIndex === oi && <span className="visually-hidden"> (the right answer)</span>}
                </label>
              </div>
            ))}
            {hasFb && (
              <div id={fbId} className="mt-2 small" data-testid={`learning-feedback-${qi}`}>
                {fb.correct !== null && (
                  <div className={`fw-semibold ${fb.correct ? "text-success-emphasis" : "text-danger-emphasis"}`}>
                    <i className={`fa-solid ${fb.correct ? "fa-check" : "fa-xmark"} me-1`} aria-hidden="true" />
                    {fb.correct ? "Correct" : "Not quite"}
                  </div>
                )}
                {fb.chosenFeedback && <div className="text-body-secondary" data-testid={`learning-option-feedback-${qi}`}>{fb.chosenFeedback}</div>}
                {fb.explanation && <div className="text-body-secondary" data-testid={`learning-explanation-${qi}`}>{fb.explanation}</div>}
              </div>
            )}
          </fieldset>
        );
      })}
      {error && (
        <p className="small text-danger" role="alert" data-testid="learning-check-error">
          {error}
        </p>
      )}
      <div aria-live="polite">
        {result && (
          <div
            ref={resultRef}
            tabIndex={-1}
            className={`alert ${result.passed ? "alert-success" : "alert-warning"} py-2`}
            data-testid="learning-check-result"
          >
            {result.passed
              ? `You scored ${result.scorePercent}%. Passed.`
              : `You scored ${result.scorePercent}%. You need ${passMark}% — read the notes under the ones marked "Not quite" and have another go.`}
          </div>
        )}
      </div>
      <button
        type="button"
        className="btn btn-primary"
        onClick={submit}
        disabled={busy || Object.keys(answers).length === 0}
        data-testid="learning-check-submit"
      >
        {busy ? "Checking…" : result && !result.passed ? "Try again" : "Check my answers"}
      </button>
    </Section>
  );
}

export default function LearningLesson({ lessonId, courseId, nav }) {
  const [lesson, setLesson] = useState(null);
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [status, setStatus] = useState("not_started");
  const [videoPct, setVideoPct] = useState(0);
  const [doneError, setDoneError] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(
    async (resumeAt = 0) => {
      setLoading(true);
      setError(null);
      const resp = await services.lmsLesson(lessonId);
      setLoading(false);
      if (!ok(resp)) {
        setError(message(resp, "This lesson couldn't be loaded, please try again."));
        return;
      }
      const l = payload(resp);
      setLesson({ ...l, __resumeAt: resumeAt });
      setStatus(l.progress?.status ?? "not_started");
      setVideoPct(l.progress?.videoPercent ?? 0);
    },
    [lessonId]
  );

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!courseId) return;
    services.lmsCourse(courseId).then((resp) => ok(resp) && setCourse(payload(resp)));
  }, [courseId, status]);

  const onVideoProgress = useCallback(
    async (percent) => {
      setVideoPct((p) => Math.max(p, percent));
      const resp = await services.lmsVideoProgress(lessonId, percent);
      if (ok(resp) && payload(resp)?.status) setStatus(payload(resp).status);
    },
    [lessonId]
  );

  const markDone = async () => {
    setBusy(true);
    setDoneError(null);
    const resp = await services.lmsLessonStudied(lessonId);
    setBusy(false);
    if (!ok(resp)) {
      setDoneError(message(resp, "That didn't save, please try again."));
      return;
    }
    setStatus(payload(resp)?.status ?? status);
  };

  if (loading && !lesson) return <p className="text-body-secondary small" data-testid="learning-loading">Loading&hellip;</p>;
  if (error)
    return (
      <>
        <button type="button" className="btn btn-link px-0 mb-3 text-decoration-none" onClick={nav.home} data-testid="learning-back">
          <i className="fa-solid fa-arrow-left me-2" aria-hidden="true" />
          My training
        </button>
        <div className="alert alert-secondary" role="alert" data-testid="learning-error">{error}</div>
      </>
    );
  if (!lesson) return null;

  const layout = lesson.layout || "text";
  const embed = !lesson.videoPlaybackUrl ? toEmbedUrl(lesson.videoUrl) : null;
  const hasVideo = Boolean(lesson.videoPlaybackUrl || embed);
  const showVideo = layout !== "text" && hasVideo;
  const showText = layout !== "video";
  const hasChecks = (lesson.checks || []).length > 0;
  const watchToFinish = layout === "video" && Boolean(lesson.videoPlaybackUrl);
  const completed = status === "completed";
  const studiedPct = lesson.videoStudiedPercent ?? 90;
  const objectives = objectivesOf(lesson);
  const quickRef = quickReferenceOf(lesson);

  const items = course?.items?.filter((i) => i.available) || [];
  const index = items.findIndex((i) => i.lessonId === lessonId);
  const nextItem = index >= 0 ? items[index + 1] : null;

  return (
    <article className="learning-lesson" data-testid="learning-lesson">
      <button
        type="button"
        className="btn btn-link px-0 mb-3 text-decoration-none"
        onClick={() => (courseId ? nav.course(courseId) : nav.home())}
        data-testid="learning-back"
      >
        <i className="fa-solid fa-arrow-left me-2" />
        {course ? course.courseName : "My training"}
      </button>

      <div className="d-flex justify-content-between align-items-start gap-3 mb-3">
        <div>
          {course && index >= 0 && (
            <div className="small text-body-secondary mb-1">
              Lesson {index + 1} of {items.length}
            </div>
          )}
          <h1 className="h4 mb-1">{lesson.title}</h1>
          <div className="small text-body-secondary d-flex flex-wrap align-items-center gap-2">
            {lesson.estimatedMinutes && <span>{formatMinutes(lesson.estimatedMinutes)}</span>}
            {lesson.release?.label && (
              <span className="badge rounded-pill bg-body-secondary text-body border learning-release" data-testid="learning-release">
                Release: {lesson.release.label}
              </span>
            )}
          </div>
        </div>
        <div className="d-flex flex-column align-items-end gap-1">
          {completed && (
            <span className="badge text-bg-success" data-testid="learning-lesson-done">
              <i className="fa-solid fa-check me-1" aria-hidden="true" />
              Done
            </span>
          )}
          {lesson.refresherDue && (
            <span className="badge text-bg-warning" data-testid="learning-refresher-due">
              <i className="fa-solid fa-rotate me-1" aria-hidden="true" />
              Refresher due
            </span>
          )}
        </div>
      </div>
      {lesson.refresherDue && (
        <p className="small text-body-secondary" data-testid="learning-refresher-note">
          This lesson changed since you finished it. Your completion still counts — go through it again to catch up on what's new.
        </p>
      )}

      {objectives.length > 0 && (
        <Section title="What you'll learn" icon="fa-solid fa-bullseye">
          <ul className="ps-3 mb-0" data-testid="learning-objectives">
            {objectives.map((o, i) => (
              <li key={i}>{o}</li>
            ))}
          </ul>
        </Section>
      )}

      {showVideo && (
        <div className="mb-4">
          {lesson.videoPlaybackUrl ? (
            <UploadedVideo
              key={lesson.videoPlaybackUrl}
              lesson={lesson}
              onProgress={onVideoProgress}
              onExpired={(at) => load(at)}
            />
          ) : (
            <div className="ratio ratio-16x9">
              <iframe
                src={embed}
                title={lesson.title}
                allow="fullscreen; picture-in-picture"
                allowFullScreen
                referrerPolicy="strict-origin-when-cross-origin"
                data-testid="learning-embed"
              />
            </div>
          )}
          <Transcript text={lesson.transcript} />
        </div>
      )}
      {layout !== "text" && !hasVideo && (
        <p className="text-body-secondary" data-testid="learning-no-video">The video for this lesson isn't available yet.</p>
      )}

      {showText && (
        <div className="learning-prose">
          {lesson.bodyWhy && (
            <Section title="Why this matters" icon="fa-solid fa-book-open">
              <p className="mb-0 learning-pre">{lesson.bodyWhy}</p>
            </Section>
          )}
          {lesson.bodyBefore && (
            <Section title="Before you start" icon="fa-solid fa-circle-check">
              <p className="mb-0 learning-pre">{lesson.bodyBefore}</p>
            </Section>
          )}
          {lesson.bodySections?.length > 0 && (
            <Section title={lesson.format === "release" ? "What's new" : lesson.format === "concept" ? "Key ideas" : "Steps"} icon="fa-solid fa-list-ol">
              <ol className="ps-3 mb-0">
                {lesson.bodySections.map((step, i) => {
                  const img = resolveImageUrl(step.imagePath, lesson.imageBucketPrefix);
                  return (
                    <li key={i} className="mb-3">
                      <div className="fw-semibold">{step.title}</div>
                      <div className="learning-pre">{step.detail}</div>
                      {step.benefit && <div className="small text-body-secondary mt-1">{step.benefit}</div>}
                      {img && <img src={img} alt={step.imageAlt || ""} className="img-fluid rounded border mt-2" loading="lazy" />}
                    </li>
                  );
                })}
              </ol>
            </Section>
          )}
          {lesson.bodyMistakes?.length > 0 && (
            <Section title="Common mistakes" icon="fa-solid fa-triangle-exclamation">
              <ul className="ps-3 mb-0">
                {lesson.bodyMistakes.map((m, i) => (
                  <li key={i} className="mb-2">
                    <span className="fw-semibold">{m.mistake}</span>
                    {m.consequence && <> — {m.consequence}</>}
                    {m.avoid && <div className="small text-body-secondary">{m.avoid}</div>}
                  </li>
                ))}
              </ul>
            </Section>
          )}
          {lesson.practiceTask && (
            <Section title="Try it yourself" icon="fa-solid fa-hand-pointer">
              <p className="mb-0 learning-pre">{lesson.practiceTask}</p>
            </Section>
          )}
        </div>
      )}

      {quickRef.length > 0 && (
        <Section title="Quick reference" icon="fa-solid fa-table-list">
          <div className="row g-3" data-testid="learning-quick-reference">
            {quickRef.map((g, gi) => (
              <div className="col-12 col-md-6" key={gi}>
                <div className="border rounded p-3 h-100 learning-quickref">
                  {g.title && <h3 className="h6 mb-2">{g.title}</h3>}
                  <ul className="ps-3 mb-0 small">
                    {g.items.map((it, ii) => (
                      <li key={ii}>{it}</li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </Section>
      )}

      {lesson.kbRefs?.length > 0 && (
        <Section title="Further learning" icon="fa-solid fa-book-atlas">
          <ul className="ps-3 mb-0">
            {lesson.kbRefs.map((ref) =>
              ref.url && /^https:\/\//.test(ref.url) ? (
                <li key={ref.id}>
                  <a href={ref.url} target="_blank" rel="noopener noreferrer">
                    {ref.title || "Knowledge base article"}
                  </a>
                </li>
              ) : (
                <li key={ref.id}>{ref.title || "Knowledge base article"}</li>
              )
            )}
          </ul>
        </Section>
      )}

      {hasChecks && (
        <Check
          lesson={lesson}
          onResult={(r) => {
            if (r.passed) setStatus("completed");
          }}
        />
      )}

      {!hasChecks && !completed && (
        <div className="mb-4">
          {watchToFinish ? (
            <p className="text-body-secondary mb-0" data-testid="learning-watch-to-finish">
              Watch to the end to finish — {Math.min(videoPct, 100)}% so far (finishes at {studiedPct}%).
            </p>
          ) : (
            <button type="button" className="btn btn-primary" onClick={markDone} disabled={busy} data-testid="learning-mark-done">
              {layout === "video" ? "Mark as watched" : "Mark as complete"}
            </button>
          )}
          {doneError && <p className="small text-danger mt-2 mb-0" role="alert">{doneError}</p>}
        </div>
      )}

      <div className="d-flex justify-content-between border-top pt-3 mt-4">
        <button type="button" className="btn btn-outline-secondary" onClick={() => (courseId ? nav.course(courseId) : nav.home())}>
          {courseId ? "Back to course" : "My training"}
        </button>
        {nextItem && (
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => nav.lesson(nextItem.lessonId, courseId)}
            data-testid="learning-next-lesson"
          >
            Next: {nextItem.title}
            <i className="fa-solid fa-arrow-right ms-2" aria-hidden="true" />
          </button>
        )}
      </div>
    </article>
  );
}
